import { useQueryClient } from "@tanstack/react-query";
import {
  BasisAdjustmentsTableRow,
  FeePostUseBreakdownBasisAdjustmentsTable,
  makeEmptyBasisAdjustmentsRow,
} from "features/fees/post/use-breakdown/FeePostUseBreakdownBasisAdjustmentsTable";
import { FeePostUseBreakdownBasisChangeTable } from "features/fees/post/use-breakdown/FeePostUseBreakdownBasisChangeTable";
import {
  getFeeChargeEventBasisChangeByUseRetrieveQueryKey,
  useFeeChargeEventRetrieveSuspense,
  useFeeChargeEventsSelectListSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import {
  getPortfolioTransactionListQueryKey,
  portfolioTransactionUpsertFeeEvent,
  usePortfolioTransactionListSuspense,
  usePortfolioTransactionUpsertFeeEvent,
} from "gen/endpoints/portfolio-transactions/portfolio-transactions";
import { PortfolioTransactionBulkCreateDataRequest } from "gen/model/portfolioTransactionBulkCreateDataRequest";
import { PortfolioTransactionCreateData } from "gen/model/portfolioTransactionCreateData";
import { get } from "lodash-es";
import log from "loglevel";
import React, { useCallback, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTableGetErrorForCellFn } from "shared/components/table/maybern/MaybernTableErrors";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { getApiErrorFromResponse } from "shared/utils/ErrorUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const isRowValid = (row: PortfolioTransactionCreateData): boolean =>
  !!row.transaction_code_id && !!row.amount && !!row.effective_date;

export const FeePostUseBreakdownPage: React.FC = () => {
  const { t } = useTranslation(["fees", "common"]);
  const queryClient = useQueryClient();

  const { navigateNext } = usePageWizard();

  const { fundFamilyId, feeChargeEventId } = useRequiredStringParams();

  const { data: feeChargeEventData } = useFeeChargeEventRetrieveSuspense(
    fundFamilyId,
    feeChargeEventId,
  );

  const fundFamilyEventId =
    feeChargeEventData.fee_charge_event.fund_family_event_id!;

  const { data: portfolioTransactionListData } =
    usePortfolioTransactionListSuspense(fundFamilyId, {
      fund_family_event_id: fundFamilyEventId,
    });

  const {
    mutateAsync: mutatePortfolioTransactionUpsert,
    error: upsertError,
    variables: upsertVariables,
  } = usePortfolioTransactionUpsertFeeEvent();

  const onSubmit = async (data: PortfolioTransactionBulkCreateDataRequest) => {
    await mutatePortfolioTransactionUpsert({
      fundFamilyId,
      data: {
        portfolio_transactions: data.portfolio_transactions.filter(isRowValid),
      },
      params: {
        upsert_fee_charge_event_transactions: true,
        fund_family_event_id: fundFamilyEventId,
      },
    });
    await invalidateQueryKeys(
      queryClient,
      getPortfolioTransactionListQueryKey(fundFamilyId),
      getFeeChargeEventBasisChangeByUseRetrieveQueryKey(
        fundFamilyId,
        feeChargeEventId,
      ),
    );
  };

  const form = useForm<PortfolioTransactionBulkCreateDataRequest>({
    defaultValues: {
      portfolio_transactions: portfolioTransactionListData.length
        ? portfolioTransactionListData
        : [makeEmptyBasisAdjustmentsRow()],
    },
  });

  const portfolioTransactions =
    useWatch<PortfolioTransactionBulkCreateDataRequest>({
      control: form.control,
      name: "portfolio_transactions",
    }) as PortfolioTransactionCreateData[];

  const onRefresh = async () => {
    try {
      // not using mutate hook here because we don't want to trigger validation if this fails in the background
      await portfolioTransactionUpsertFeeEvent(
        fundFamilyId,
        {
          portfolio_transactions: portfolioTransactions,
        },
        { fund_family_event_id: fundFamilyEventId },
      );
    } catch (e) {
      log.error("portfolio transaction upsert failed", e);
    }
  };

  const getErrorForCell = useCallback<
    MaybernTableGetErrorForCellFn<BasisAdjustmentsTableRow>
  >(
    (params) => {
      const errorData = getApiErrorFromResponse(upsertError)?.data;
      if (!errorData) {
        return undefined;
      }

      // ensure that errors are displayed in the correct row after other rows are added or removed
      const indexWhenSubmitted =
        upsertVariables?.data.portfolio_transactions.findIndex(
          // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
          (row) => get(row, "id") === params.node.id,
        ) ?? -1;
      if (indexWhenSubmitted === -1) {
        return undefined;
      }

      const key = `portfolio_transactions.${indexWhenSubmitted}.${params.column!.getColId()}`;

      // don't show error if the value has changed since submission
      if (upsertVariables) {
        const submittedValue = get(upsertVariables.data, key);
        const currentValue = get(
          params.node.data,
          params.column!.getColId(),
        ) as string | undefined;
        if (submittedValue !== currentValue) {
          return undefined;
        }
      }

      const fieldErrors = get(errorData, key) as string[] | undefined;
      return fieldErrors?.join(", ");
    },
    [upsertVariables, upsertError],
  );
  const { data: feePeriodSelectData } =
    useFeeChargeEventsSelectListSuspense(fundFamilyId);
  const [feeChargeEventSelected, setFeeChargeEventSelected] =
    useState(feeChargeEventId);

  return (
    <PageContent>
      <PageHeader
        title={t("fees:feeUseBreakdown.pageHeader.title")}
        workflowName={t("fees:feeUseBreakdown.pageHeader.workflowName")}
        helperText={t("fees:feeUseBreakdown.pageHeader.helperText")}
        badge={{
          type: "default",
          text: feeChargeEventData.fee_charge_daterange_rich_name,
        }}
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={navigateNext}
          excludeNonFieldErrors={["portfolio_transactions"]}
        >
          <TableBox>
            <TableBoxHeader>
              <TableBoxTitle>
                {t("fees:feeUseBreakdown.basisAdjustmentsTable.title")}
              </TableBoxTitle>
              <TableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <FeePostUseBreakdownBasisAdjustmentsTable
                fundFamilyId={fundFamilyId}
                getErrorForCell={getErrorForCell}
                form={form}
              />
            </TableBoxBody>
          </TableBox>
          <FeePostUseBreakdownBasisChangeTable
            fundFamilyId={fundFamilyId}
            feeChargeEventId={feeChargeEventSelected}
            onRefresh={onRefresh}
            setFeeChargeEventSelected={setFeeChargeEventSelected}
            feePeriodSelectData={feePeriodSelectData}
          />
          <PageWizardFormFooter />
        </Form>
      </PageBody>
    </PageContent>
  );
};
